import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Slide,
  Snackbar,
  Typography,
} from "@material-ui/core"
import CloseIcon from "@material-ui/icons/Close"
import { useContext, useEffect, useRef, useState } from "react"
import { useHistory } from "react-router"
import AppContext from "~/contexts/App"
import LearningCuesContext from "~/contexts/LearningCues"
import LessonContext from "~/contexts/Lesson"
import useHotkeys from "~/hooks/useHotkeys"
import checkMetaKey from "~/utils/checkMetaOrCtrl"

import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import useReadOnly from "~/hooks/useReadOnly"
import useStyles from "./styles"

function TransitionDown(props) {
  return <Slide {...props} direction="up" />
}

const UnsavedChanges = () => {
  const classes = useStyles()
  const isReadOnly = useReadOnly()

  const blockRef = useRef()

  const [openSaveSnack, setOpenSaveSnack] = useState(false)
  const [openErrorSnack, setOpenErrorSnack] = useState(false)
  const [errorText, setErrorText] = useState("")
  const [dismissed, setDismissed] = useState(false)
  const history = useHistory()
  const [transition, setTransition] = useState(undefined)
  const {
    unsavedState,
    handleUpdate,
    saveLoading,
    discardChanges,
    tryPublish,
    disabledSave,
    lesson,
    isLearningCuePastDuration,
  } = useContext(LessonContext)
  const { openDialog } = useContext(AppContext)
  const { backwardSteps, saveLearningCues, resetLearningCues } =
    useContext(LearningCuesContext)

  const saveHotKey = useHotkeys("meta+s")

  const beforeSave = (func) => () => {
    if (history.location.pathname.indexOf("/cues") > -1) {
      saveLearningCues({
        onSettled: () => {
          setOpenSaveSnack(null)
        },
      })
    } else {
      func()
    }
  }

  //open error snack bar if the user need to complete more data before publish
  useEffect(() => {
    if (tryPublish) {
      if (disabledSave) {
        setErrorText("Please complete all required inputs.")
        setOpenErrorSnack(true)
        setTransition(() => TransitionDown)
      } else if (isLearningCuePastDuration) {
        setErrorText("One or more lesson cues starts or ends after video ends.")
        setOpenErrorSnack(true)
        setTransition(() => TransitionDown)
      } else {
        setOpenErrorSnack(false)
      }
    } else if (isLearningCuePastDuration) {
      setErrorText("One or more lesson cues starts or ends after video ends.")
      setOpenErrorSnack(true)
      setTransition(() => TransitionDown)
    } else if ((!disabledSave || !tryPublish) && !isLearningCuePastDuration) {
      setOpenErrorSnack(false)
    }
  }, [disabledSave, tryPublish, isLearningCuePastDuration])

  //prevent reload
  useEffect(() => {
    if (unsavedState.length > 0 || backwardSteps.length > 1) {
      window.onbeforeunload = function () {
        return "Unsaved Data"
      }
    } else {
      window.onbeforeunload = function () {
        return null
      }
    }
  }, [unsavedState, backwardSteps])

  //open save snack bar if we have unsaved data
  useEffect(() => {
    if (unsavedState.length > 0 || backwardSteps.length > 1) {
      setOpenSaveSnack(true)
      setTransition(() => TransitionDown)
    } else if (unsavedState.length === 0) {
      setOpenSaveSnack(false)
      setDismissed(false)
    }
  }, [unsavedState, backwardSteps])

  //open dialog if we go to other part of the app without save
  useEffect(() => {
    if ((unsavedState.length > 0 || backwardSteps.length > 1) && !isReadOnly) {
      blockRef.current = {
        unblock: history.block((tx) => {
          if (
            tx.pathname.includes("settings") || // allow navigating to settings
            tx.pathname.split("/").length === 3 //   or index with unsaved changes
          ) {
            blockRef.current.unblock()
            history.push(tx.pathname)
          } else {
            openDialog("unsavedDataDialog", tx).then(() => {
              discardChanges() // for clearing lesson details (except cues) back to last saved state
              resetLearningCues(backwardSteps[0]) // for returning back to last saved learning cues state
              blockRef.current.unblock()
              // tx.retry();
              history.push(tx.pathname)
            })
          }
          return false
        }),
      }
    } else {
      blockRef.current?.unblock?.()
    }
  }, [unsavedState, backwardSteps, history.location.pathname])

  useEffect(() => {
    document.onkeydown = function (e) {
      if (e.code === "KeyS" && checkMetaKey(e)) {
        e.preventDefault()
        handleUpdate()
      }
    }
    return () => {
      document.onkeydown = () => ""
    }
  }, [handleUpdate])

  return (
    <PermissionsWrapper permissionGroup="admin">
      <>
        <Snackbar
          open={openSaveSnack && !dismissed}
          classes={{ root: classes.snackBar }}
          message={<Typography variant="subtitle1">Unsaved Changes</Typography>}
          className={classes.snackBar}
          TransitionComponent={transition}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          action={
            <>
              {saveLoading ? (
                <Box display="flex" justifyContent="center" width="100%">
                  <CircularProgress style={{ color: "#fff" }} size={18} />
                </Box>
              ) : (
                <Button
                  color="secondary"
                  size="small"
                  onClick={beforeSave(handleUpdate)}
                >
                  SAVE
                </Button>
              )}
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setDismissed(true)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        />
        <Snackbar
          open={openErrorSnack}
          message={<Typography variant="subtitle1">{errorText}</Typography>}
          ContentProps={{
            "aria-describedby": "message-id",
            className: classes.snackbarStyleViaContentProps,
          }}
          style={openSaveSnack ? { marginBottom: "80px" } : null}
          classes={{ root: classes.errorSnackBar }}
          TransitionComponent={transition}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          action={
            <>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setOpenErrorSnack(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        />
      </>
    </PermissionsWrapper>
  )
}

export default UnsavedChanges
